import { motion, AnimatePresence } from "framer-motion";
import { useEffect, useState, useRef, useMemo } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { isRTL, getTextDirection, getRTLClasses, getDirAttribute } from "../../utils/rtl";
import { ContentLabels } from "../../types/navigation";

interface LocationProps {
  projectName: string;
  coordinates: {
    lat: number;
    lng: number;
  };
  nearbyPlaces: {
    category: "education" | "healthcare" | "lifestyle" | "transport" | "worship";
    name: string;
    distance: string;
    time: string;
  }[];
  keyDistances?: {
    place: string;
    time: string;
  }[];
  language?: string;
  contentLabels?: ContentLabels;
}

const IconComponent = ({ name }: { name: string }) => {
  const icons = {
    education: (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
        <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
      </svg>
    ),
    healthcare: (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 12h-4l-3 9L9 3l-3 9H2"></path>
      </svg>
    ),
    lifestyle: (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="10"></circle>
        <path d="M12 16v-4"></path>
        <path d="M12 8h.01"></path>
      </svg>
    ),
    transport: (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="3" y="4" width="18" height="12" rx="2" ry="2"></rect>
        <circle cx="8" cy="20" r="1"></circle>
        <circle cx="16" cy="20" r="1"></circle>
        <path d="M4 12h16"></path>
      </svg>
    ),
    worship: (
      <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z"></path>
        <line x1="4" y1="22" x2="4" y2="15"></line>
      </svg>
    )
  };

  return icons[name as keyof typeof icons] || null;
};

const categoryIcons = {
  education: "education",
  healthcare: "healthcare",
  lifestyle: "lifestyle",
  transport: "transport",
  worship: "worship"
};

const Location = ({
  projectName = "Aqua Flora",
  coordinates = { lat: 25.069750, lng: 55.247471 },
  nearbyPlaces = [],
  keyDistances = [
    { place: "Dubai Mall", time: "15 mins" },
    { place: "Dubai Airport", time: "20 mins" },
    { place: "Palm Jumeirah", time: "25 mins" }
  ],
  language = "en",
  contentLabels
}: LocationProps) => {
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const [isMapInteractive, setIsMapInteractive] = useState(false);
  const [touchCount, setTouchCount] = useState(0);
  const mapRef = useRef<L.Map | null>(null);

  // Default fallback labels if contentLabels is not provided
  const labels = contentLabels || {
    primeLocation: 'Prime Location',
    nearbyPlaces: 'Nearby Places',
    keyDistances: 'Key Distances',
    noNearbyPlaces: 'No nearby places available.',
    noKeyDistances: 'No key distances available.',
    // Other required fields with fallbacks
    availableUnitTypes: 'Available Unit Types',
    unitSize: 'Size:',
    startingFrom: 'Starting from',
    downloadBrochure: 'Download Brochure',
    contactExpert: 'Contact Expert',
    viewDetails: 'View Details',
    requestInfo: 'Request Information',
    bookTour: 'Book a Tour',
    whatsappAction: 'WhatsApp',
    callAction: 'Call Now',
    learnMore: 'Learn More',
    viewAll: 'View All',
    showMore: 'Show More',
    showLess: 'Show Less',
    paymentPlan: 'Payment Plan',
    downPayment: 'Down Payment',
    dueBy: 'Due by',
    noPaymentPlans: 'No payment plans available at this time.',
    frequentlyAskedQuestions: 'Frequently Asked Questions',
    faqDescription: 'Find answers to common questions about our property and investment opportunities',
    projectGallery: 'Project Gallery',
    closeGallery: 'Close gallery',
    dldPermitNumber: 'DLD Permit Number',
    scanQrCode: 'Scan QR Code for more information',
    fullName: 'Full Name',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email Address',
    phoneNumber: 'Phone Number',
    continue: 'Continue',
    back: 'Back',
    submit: 'Submit',
    submitting: 'Submitting...',
    thankYou: 'Thank You!',
    nameRequired: 'Name is required',
    emailRequired: 'Email is required',
    emailInvalid: 'Please enter a valid email',
    phoneRequired: 'Phone number is required'
  };

  // RTL detection and classes
  const rtlClasses = getRTLClasses(language);
  const dirAttribute = getDirAttribute(language);

  const handleMapInteraction = (interactive: boolean) => {
    if (mapRef.current) {
      if (interactive) {
        mapRef.current.scrollWheelZoom.enable();
        mapRef.current.dragging.enable();
      } else {
        mapRef.current.scrollWheelZoom.disable();
        mapRef.current.dragging.disable();
      }
      setIsMapInteractive(interactive);
    }
  };

  // Group nearby places by category
  const nearbyPlacesByCategory = useMemo(() => {
    const groupedPlaces: Record<string, typeof nearbyPlaces> = {
      education: [],
      healthcare: [],
      lifestyle: [],
      transport: [],
      worship: []
    };
    
    nearbyPlaces.forEach(place => {
      if (groupedPlaces[place.category]) {
        groupedPlaces[place.category].push(place);
      }
    });
    
    return groupedPlaces;
  }, [nearbyPlaces]);

  // Get unique categories that have places
  const availableCategories = useMemo(() => {
    return Object.keys(nearbyPlacesByCategory).filter(
      category => nearbyPlacesByCategory[category].length > 0
    );
  }, [nearbyPlacesByCategory]);

    useEffect(() => {
    if (typeof window !== "undefined") {
      const mapInstance = L.map("map", {
        dragging: !L.Browser.mobile,
        scrollWheelZoom: false,
        doubleClickZoom: false,
        touchZoom: true, // Enable pinch zoom
        zoomControl: false // We'll add zoom control in a better position
      }).setView([coordinates.lat, coordinates.lng], 15);
      
      mapRef.current = mapInstance;

      // Add zoom control to the top-right
      L.control.zoom({
        position: 'topright'
      }).addTo(mapInstance);
      
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
        attribution: '© OpenStreetMap contributors'
      }).addTo(mapInstance);

      const markerHtml = `
        <div style="
          width: 30px;
          height: 42px;
          border-radius: 50% 50% 50% 0;
          background: hsl(var(--primary));
          position: absolute;
          transform: rotate(-45deg);
          left: 50%;
          top: 50%;
          margin: -15px 0 0 -15px;
          display: flex;
          align-items: center;
          justify-content: center;
        "></div>
      `;

      const customIcon = L.divIcon({
        html: markerHtml,
        iconSize: [30, 42],
        iconAnchor: [15, 42],
        className: ''
      });

      L.marker([coordinates.lat, coordinates.lng], { icon: customIcon })
        .addTo(mapInstance)
        .bindPopup(`<b>${projectName}</b><br>Your Future Home`)
        .openPopup();

      // Handle touch events for better mobile UX
      const mapElement = document.getElementById('map');
      if (mapElement) {
        mapElement.addEventListener('touchstart', (e) => {
          setTouchCount(e.touches.length);
          if (e.touches.length >= 2) {
            // Enable map interaction for multi-touch
            handleMapInteraction(true);
            e.stopPropagation();
          }
        }, { passive: false });

        mapElement.addEventListener('touchend', (e) => {
          setTouchCount(e.touches.length);
          if (e.touches.length < 2 && !isMapInteractive) {
            handleMapInteraction(false);
          }
        }, { passive: false });
      }

            return () => {
        mapInstance.remove();
        mapElement?.removeEventListener('touchstart', () => {});
        mapElement?.removeEventListener('touchend', () => {});
      };
    }
  }, [coordinates, projectName, handleMapInteraction]);

  return (
    <section className={`py-16 md:py-24 bg-background ${rtlClasses}`} dir={dirAttribute}>
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className={`text-3xl md:text-4xl font-bold mb-4 ${rtlClasses ? 'text-right' : 'text-center'}`}>
            {labels.primeLocation}
          </h2>
          <p className={`text-muted-foreground max-w-2xl mx-auto ${rtlClasses ? 'text-right' : 'text-center'}`}>
            Discover the perfect balance of connectivity and tranquility at {projectName}. 
            Strategically located for your convenience.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-8">
          {/* Map Container with Interaction Overlay */}
          <div className="md:col-span-2 rounded-xl overflow-hidden shadow-lg h-[500px] relative">
            <div id="map" className="w-full h-full" />
            
            {/* Touch Instruction Overlay */}
            {!isMapInteractive && touchCount === 0 && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute inset-0 bg-foreground/5 backdrop-blur-[2px] flex items-center justify-center cursor-pointer"
                onClick={() => handleMapInteraction(true)}
              >
                <div className="bg-background/80 backdrop-blur-sm px-6 py-4 rounded-lg shadow-lg text-center max-w-xs">
                  <div className="flex justify-center mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                    </svg>
                  </div>
                  <p className="text-sm font-medium text-foreground">Tap to interact with map</p>
                  <p className="text-xs text-muted-foreground mt-1">Use two fingers to zoom and pan</p>
                  <div className="mt-3 flex justify-center gap-2">
                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-primary/10 text-primary text-xs">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Zoom
                    </span>
                    <span className="inline-flex items-center px-2 py-1 rounded-md bg-primary/10 text-primary text-xs">
                      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                      Pan
                    </span>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Exit Button */}
            {isMapInteractive && (
              <motion.button
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: 10 }}
                className="absolute top-4 right-4 bg-background/80 backdrop-blur-sm px-3 py-1.5 rounded-lg shadow-lg text-sm font-medium text-foreground hover:bg-background/90 transition-colors"
                onClick={() => handleMapInteraction(false)}
              >
                Exit Map
              </motion.button>
            )}

            {/* Zoom Instructions */}
            {isMapInteractive && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="absolute bottom-4 left-4 right-4 flex justify-center"
              >
                <div className="bg-background/80 backdrop-blur-sm px-4 py-2 rounded-lg shadow-lg text-xs text-muted-foreground">
                  {L.Browser.mobile ? "Use two fingers to zoom • Double tap to zoom in" : "Use scroll wheel to zoom • Double click to zoom in"}
                </div>
              </motion.div>
            )}
          </div>

          {/* Info Panel */}
          <div className="space-y-6">
            {/* Category Filters */}
            <div className="bg-card rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-semibold mb-4 text-card-foreground">{labels.nearbyPlaces}</h3>
              
              {availableCategories.length > 0 ? (
              <div className="space-y-3">
                  {availableCategories.map(category => (
                  <div key={category}>
                    <motion.button
                      onClick={() => setActiveCategory(activeCategory === category ? null : category)}
                      className={`w-full text-left px-4 py-3 rounded-lg flex items-center space-x-3 transition-colors ${
                        activeCategory === category
                          ? "bg-primary/10 text-primary"
                          : "hover:bg-accent text-card-foreground hover:text-accent-foreground"
                      }`}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                        <IconComponent name={category as keyof typeof categoryIcons} />
                      <span className="capitalize">{category}</span>
                    </motion.button>
                    
                    <AnimatePresence>
                      {activeCategory === category && (
                        <motion.div
                          initial={{ opacity: 0, height: 0 }}
                          animate={{ opacity: 1, height: "auto" }}
                          exit={{ opacity: 0, height: 0 }}
                          transition={{ duration: 0.2 }}
                          className="mt-2 ml-11 space-y-2"
                        >
                            {nearbyPlacesByCategory[category].map((place, index) => (
                            <motion.div
                                key={`${place.name}-${index}`}
                              initial={{ opacity: 0, x: -10 }}
                              animate={{ opacity: 1, x: 0 }}
                              exit={{ opacity: 0, x: -10 }}
                              transition={{ delay: index * 0.1 }}
                              className="text-sm"
                            >
                              <p className="font-medium text-card-foreground">{place.name}</p>
                              <p className="text-muted-foreground text-xs">
                                {place.distance} • {place.time}
                              </p>
                            </motion.div>
                          ))}
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                ))}
              </div>
              ) : (
                <p className="text-muted-foreground text-sm">{labels.noNearbyPlaces}</p>
              )}
            </div>

            {/* Key Distances */}
            <div className="bg-card rounded-xl shadow-lg p-6">
              <h3 className="text-xl font-semibold mb-4 text-card-foreground">{labels.keyDistances}</h3>
              <div className="space-y-3">
                {keyDistances.length > 0 ? (
                  keyDistances.map((item, index) => (
                    <div key={`distance-${index}`} className="flex justify-between items-center">
                      <span className="text-card-foreground">{item.place}</span>
                      <span className="text-muted-foreground">{item.time}</span>
                </div>
                  ))
                ) : (
                  <p className="text-muted-foreground text-sm">{labels.noKeyDistances}</p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Global Click Handler */}
      {isMapInteractive && (
        <div 
          className="fixed inset-0 z-[998]" 
          onClick={(e) => {
            if (!document.getElementById('map')?.contains(e.target as Node)) {
              handleMapInteraction(false);
            }
          }}
        />
      )}
    </section>
  );
};

export default Location; 